import React, { useMemo, useState } from "react";
import {
  AlertTriangle, Check, ChevronDown, ChevronRight, ChevronUp, EyeOff,
  Loader2, RefreshCw, Trash2, Upload, X,
} from "lucide-react";
import {
  PANEL_SECTION_ORDER, SECTION_LABELS,
  type DeltaEntry, type EmailDiff, type ForecastComparisonDoc,
  type ForecastRunSummary, type SectionDiff, type SectionKey,
} from "../../forecast/types";

const TRACKER_SUB_ORDER: Array<[string, string]> = [
  ["timeline", "Timeline"],
  ["issues", "Issues"],
  ["commitments", "Commitments"],
  ["diligence", "Diligence"],
  ["wires", "Wires"],
  ["signatures", "Signatures"],
  ["closingItems", "Closing"],
];

const scoreColor = (s: number): string =>
  s >= 0.9 ? "var(--green)" : s >= 0.7 ? "var(--amber)" : "var(--red)";

const pct = (s: number): string => Math.round(s * 100) + "%";

function SectionBadge({ d }: { d: SectionDiff }): JSX.Element {
  if (d.noForecast) return <span className="fcst-badge" style={{ color: "var(--tert)" }}>No forecast</span>;
  if (d.score === null) return <span className="fcst-badge" style={{ color: "var(--tert)" }}>—</span>;
  const misses = d.deltas.filter((x) => x.kind !== "match").length;
  if (d.score >= 0.9999 && misses === 0)
    return (
      <span className="fcst-badge" style={{ color: "var(--green)" }}>
        <Check size={11} /> Match
      </span>
    );
  const red = d.score < 0.5;
  return (
    <span className="fcst-badge" style={{ color: red ? "var(--red)" : "var(--amber)" }}>
      {red ? <X size={11} /> : <AlertTriangle size={11} />} {misses} delta{misses === 1 ? "" : "s"}
    </span>
  );
}

function DeltaRow({ d }: { d: DeltaEntry }): JSX.Element {
  const ok = d.kind === "match";
  const label = d.path.split(".").slice(1).join(" › ") || d.path;
  return (
    <div className="fcst-delta">
      <div className="fcst-deltahd">
        {ok ? (
          <Check size={11} style={{ color: "var(--green)", flex: "none" }} />
        ) : (
          <X size={11} style={{ color: "var(--red)", flex: "none" }} />
        )}
        <span className="mono fcst-path" title={d.path}>{label}</span>
      </div>
      {!ok && (
        <div className="fcst-vs">
          <div>
            <span className="fcst-vslabel">Forecast</span>
            <span>{d.forecast ?? <em className="tert">(not predicted)</em>}</span>
          </div>
          <div>
            <span className="fcst-vslabel">Actual</span>
            <span>{d.actual ?? <em className="tert">(not detected)</em>}</span>
          </div>
        </div>
      )}
      {ok && d.forecast && <div className="fcst-okval">{d.forecast}</div>}
    </div>
  );
}

function SectionBody({ d }: { d: SectionDiff }): JSX.Element {
  if (d.noForecast)
    return <div className="fcst-empty">Section not predicted — excluded from scoring.</div>;
  if (d.score === null)
    return <div className="fcst-empty">Both forecast and actual empty — excluded from scoring.</div>;
  if (d.section === "trackers") {
    return (
      <>
        {TRACKER_SUB_ORDER.map(([key, label]) => {
          const rows = d.deltas.filter((x) => x.path.split(".")[2] === key);
          if (!rows.length) return null;
          return (
            <div key={key}>
              <div className="fcst-subhd">{label}</div>
              {rows.map((x, i) => (
                <DeltaRow key={i} d={x} />
              ))}
            </div>
          );
        })}
      </>
    );
  }
  return (
    <>
      {d.deltas.map((x, i) => (
        <DeltaRow key={i} d={x} />
      ))}
    </>
  );
}

export interface ForecastPanelProps {
  comparison: ForecastComparisonDoc | null;
  forecasts: ForecastRunSummary[];
  selectedForecastId: string | null;
  setSelectedForecastId: (id: string) => void;
  /** Email currently selected in the feed (falls back to latest processed). */
  emailId: number | null;
  totalEmails: number;
  processedCount: number;
  computing: boolean;
  onRecompute: () => void;
  onHide: () => void;
  /** Open the upload modal. Omitted when the matter can't accept an upload
      (e.g. corpus not yet synced to the backend), which hides the CTA. */
  onUpload?: () => void;
  onDelete: (id: string) => void;
}

/* Fourth simulation column (spec §7): per-email forecast-vs-actual diff with
   collapsible sections mirroring the right-column tab order, plus an
   always-visible run summary footer. */
export function ForecastPanel(props: ForecastPanelProps): JSX.Element {
  const {
    comparison, forecasts, selectedForecastId, setSelectedForecastId,
    emailId, totalEmails, processedCount, computing, onRecompute, onHide, onUpload, onDelete,
  } = props;
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [summaryOpen, setSummaryOpen] = useState<boolean>(false);

  const diff: EmailDiff | null = useMemo(() => {
    if (!comparison || !comparison.emailDiffs.length) return null;
    if (emailId !== null) {
      const hit = comparison.emailDiffs.find((d) => d.emailId === emailId);
      if (hit) return hit;
    }
    return comparison.emailDiffs[comparison.emailDiffs.length - 1];
  }, [comparison, emailId]);

  const summary = comparison?.summary;
  const sectionStats = useMemo(() => {
    if (!summary) return [];
    return Object.entries(summary.sectionAverages)
      .map(([k, v]) => ({ key: k as SectionKey, avg: v }))
      .sort((a, b) => a.avg - b.avg);
  }, [summary]);
  const weakest = sectionStats[0];
  const strongest = sectionStats[sectionStats.length - 1];

  return (
    <section className="scol fcst-col" aria-label="Forecast comparison">
      <div className="scolhd">
        <span className="t">Forecast</span>
        {forecasts.length > 1 && (
          <select
            className="fcst-select"
            value={selectedForecastId || ""}
            onChange={(e) => setSelectedForecastId(e.target.value)}
            aria-label="Select forecast run"
          >
            {forecasts.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name || f.id.slice(0, 8)}
              </option>
            ))}
          </select>
        )}
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 4 }}>
          <button
            className="icon-btn"
            title="Recompute comparison"
            onClick={onRecompute}
            disabled={computing || !selectedForecastId || processedCount === 0}
          >
            {computing ? <Loader2 size={14} className="spinrot" /> : <RefreshCw size={14} />}
          </button>
          {selectedForecastId && (
            <button
              className="icon-btn"
              title="Delete this forecast"
              onClick={() => onDelete(selectedForecastId)}
            >
              <Trash2 size={14} />
            </button>
          )}
          <button className="icon-btn" title="Hide forecast panel" onClick={onHide}>
            <EyeOff size={14} />
          </button>
        </span>
      </div>

      <div className="scolbody">
        {!comparison || !diff ? (
          <div className="fcst-empty" style={{ padding: 16 }}>
            {forecasts.length === 0 ? (
              <>
                <div>No forecast uploaded for this matter yet.</div>
                <div style={{ marginTop: 6 }}>
                  Export the corpus, generate a prediction JSON, then upload it to
                  compare the forecast against the live run.
                </div>
                {onUpload && (
                  <button className="btn btn-sm" style={{ marginTop: 12 }} onClick={onUpload}>
                    <Upload size={13} /> Upload forecast
                  </button>
                )}
              </>
            ) : processedCount === 0 ? (
              "Run the simulation — the comparison computes as emails are processed."
            ) : computing ? (
              "Computing comparison…"
            ) : (
              "No comparison yet. Press ⟳ to compute."
            )}
          </div>
        ) : (
          <>
            <div className="fcst-score">
              <div className="fcst-scorehd">
                <span>
                  Email #{diff.emailId} of {totalEmails}
                </span>
                <span className="mono" style={{ color: scoreColor(diff.overallScore) }}>
                  {pct(diff.overallScore)} match
                </span>
              </div>
              <div className="fcst-bar">
                <div
                  className="fcst-barfill"
                  style={{
                    width: pct(diff.overallScore),
                    background: scoreColor(diff.overallScore),
                  }}
                />
              </div>
            </div>

            {PANEL_SECTION_ORDER.map((key) => {
              const d = diff.sections.find((s) => s.section === key);
              if (!d) return null;
              const isOpen = !!open[key];
              return (
                <div key={key} className="fcst-sec">
                  <button
                    className="fcst-sechd"
                    aria-expanded={isOpen}
                    onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}
                  >
                    {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                    <span className="fcst-secname">{SECTION_LABELS[key]}</span>
                    <SectionBadge d={d} />
                  </button>
                  {isOpen && (
                    <div className="fcst-secbody">
                      <SectionBody d={d} />
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {summary && (
        <div className="fcst-summary">
          <button
            className="fcst-sumhd"
            aria-expanded={summaryOpen}
            onClick={() => setSummaryOpen((v) => !v)}
          >
            <span className="t">Summary (all emails)</span>
            {summaryOpen ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
          </button>
          <div className="fcst-sumline mono">
            Overall: <span style={{ color: scoreColor(summary.overallScore) }}>{pct(summary.overallScore)}</span>
            {" · "}
            {summary.perfectMatches}/{comparison!.emailDiffs.length} perfect
            {summary.majorDivergences > 0 && (
              <> · <span style={{ color: "var(--red)" }}>{summary.majorDivergences} diverged</span></>
            )}
          </div>
          {weakest && strongest && weakest.key !== strongest.key && (
            <div className="fcst-sumline">
              Weakest: {SECTION_LABELS[weakest.key]} ({pct(weakest.avg)}) · Strongest:{" "}
              {SECTION_LABELS[strongest.key]} ({pct(strongest.avg)})
            </div>
          )}
          {summaryOpen && (
            <div style={{ marginTop: 6 }}>
              {sectionStats.map((s) => (
                <div key={s.key} className="fcst-sumrow">
                  <span>{SECTION_LABELS[s.key]}</span>
                  <span className="mono" style={{ color: scoreColor(s.avg) }}>{pct(s.avg)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
